import React from 'react';
import { ErrorBoundary as DatadogErrorBoundary } from '@datadog/browser-rum-react';

// All this code is due to desiring to use this new ErrorBoundary component,
// whilst also not having to update all the current usages of ErrorBoundary

// Define the type for Datadog's ErrorBoundary fallback component
// Based on real-world usage in Uniswap and JesusFilm projects
type DatadogErrorFallbackProps = {
  error: Error;
  resetError: () => void;
};

type DatadogFallbackComponent = React.ComponentType<DatadogErrorFallbackProps>;

// Default fallback component that matches Datadog's expected signature
const DefaultErrorFallback: React.FC<DatadogErrorFallbackProps> = () => <div />;

// Helper function to create a component from a ReactNode
const createFallbackComponent = (
  node: React.ReactNode,
): DatadogFallbackComponent => {
  // Return a component that renders the node
  const FallbackWrapper: React.FC<DatadogErrorFallbackProps> = () => (
    <>{node}</>
  );
  return FallbackWrapper;
};

// Wrapper component that provides a default fallback
// The Datadog ErrorBoundary requires a fallback prop of type Fallback (a component, not an element)
export const ErrorBoundary: React.FC<{
  children: React.ReactNode;
  fallback?: DatadogFallbackComponent | React.ReactNode;
}> = ({ children, fallback }) => {
  // Convert ReactNode to a component if needed
  const errorFallbackComponent: DatadogFallbackComponent = React.useMemo(() => {
    if (!fallback) {
      return DefaultErrorFallback;
    }

    // If fallback is already a component, use it directly
    if (typeof fallback === 'function') {
      return fallback as DatadogFallbackComponent;
    }

    // If fallback is a React element/node, wrap it in a component
    return createFallbackComponent(fallback);
  }, [fallback]);

  return (
    <DatadogErrorBoundary fallback={errorFallbackComponent}>
      {children}
    </DatadogErrorBoundary>
  );
};

export default ErrorBoundary;
